import { TaskType } from '@/shared/domain/taskTypes';

export const NER_DEFAULT_ENTITY_THRESHOLD = 0.0;
const SYNTHETIC_STEPS = 21;

export function isNerEntitiesThresholdContext(taskType, activeTab) {
  return (
    taskType === TaskType.NER ||
    (taskType === TaskType.NER_WITH_RELATIONS && activeTab === 'entities')
  );
}

function emptyCandidate(threshold) {
  return {
    threshold,
    precision: 0,
    recall: 0,
    f_beta_score: 0,
    accuracy: 0,
    confusion_matrix: { tn: 0, fp: 0, fn: 0, tp: 0 },
  };
}

export function syntheticNerThresholdCandidates(optimalThreshold = NER_DEFAULT_ENTITY_THRESHOLD) {
  const candidates = [];
  for (let i = 0; i < SYNTHETIC_STEPS; i += 1) {
    candidates.push(emptyCandidate(i / (SYNTHETIC_STEPS - 1)));
  }
  const optimal = Number.isFinite(Number(optimalThreshold))
    ? Number(optimalThreshold)
    : NER_DEFAULT_ENTITY_THRESHOLD;
  if (!candidates.some((candidate) => Math.abs(candidate.threshold - optimal) < 1e-9)) {
    candidates.push(emptyCandidate(optimal));
    candidates.sort((left, right) => left.threshold - right.threshold);
  }
  return candidates;
}

export function mergeNerThresholdCandidates(thresholdCandidates, labels, extras = {}) {
  const calibrated = { ...(thresholdCandidates?.calibrated || {}) };
  const selectedThresholds = extras.selectedThresholds || {};
  for (const label of labels || []) {
    const existing = calibrated[label];
    if ((existing?.candidates?.length || 0) > 0) continue;
    const optimal =
      existing?.optimal_threshold ??
      selectedThresholds[label] ??
      NER_DEFAULT_ENTITY_THRESHOLD;
    calibrated[label] = {
      ...(existing || {}),
      optimal_threshold: optimal,
      candidates: syntheticNerThresholdCandidates(optimal),
    };
  }
  return {
    ...thresholdCandidates,
    calibrated,
  };
}

export function nerBaselineThresholds(labels, evaluationResult) {
  const config = evaluationResult?.selected_threshold_config;
  const saved = evaluationResult?.selected_thresholds;
  if (config === 'custom' && saved) return saved;

  const calibrated = evaluationResult?.threshold_candidates?.calibrated || {};
  const defaults = {};
  const keys = new Set([...(labels || []), ...Object.keys(calibrated)]);
  keys.forEach((label) => {
    if (calibrated[label]?.optimal_threshold != null) {
      defaults[label] = calibrated[label].optimal_threshold;
    } else if (saved?.[label] != null) {
      defaults[label] = saved[label];
    } else {
      defaults[label] = NER_DEFAULT_ENTITY_THRESHOLD;
    }
  });
  return Object.keys(defaults).length ? defaults : null;
}

/**
 * Merge live `/compute-metrics-for-thresholds` sample counters onto the stored
 * Evaluation pie payload. Stored counters are nested
 * `{ status: { oldModel, newModel } }` via `_compare_2_dicts`; live NER/class
 * metrics return flat `{ status: number }`. Overlay only the student
 * (`newModel`) side so teacher/compare slices stay put while the Overview pie
 * tracks the threshold slider.
 */
export function overlayLiveSampleCounters(storedCounters, liveCounters) {
  if (!liveCounters || typeof liveCounters !== 'object') {
    return storedCounters;
  }
  const out = { ...(storedCounters || {}) };
  Object.entries(liveCounters).forEach(([status, value]) => {
    if (typeof value === 'number') {
      out[status] = { ...(out[status] || {}), newModel: value };
      return;
    }
    if (value && typeof value === 'object') {
      out[status] = { ...(out[status] || {}), ...value };
    }
  });
  return out;
}

/** Sample counters shown on Overview pies while Adjust Threshold is live. */
export function resolveOverviewSampleCounters(storedCounters, customThresholdMetrics) {
  return overlayLiveSampleCounters(
    storedCounters,
    customThresholdMetrics?.eval_sample_counters,
  );
}

export function resolvePlayWithThresholdLabel({
  taskType,
  activeTab,
  isMultilabel,
  evaluationResult,
  classOptions,
  allLabelsValue,
}) {
  const isClassification = taskType === TaskType.classification;
  const isNerEntities = isNerEntitiesThresholdContext(taskType, activeTab);
  if (!isClassification && !isNerEntities) return null;

  const calibrated = evaluationResult?.threshold_candidates?.calibrated || {};
  const rejectionLabel = evaluationResult?.rejection_label_name;
  const options = classOptions || [];

  const firstOption = (requireCandidates) =>
    options.find(
      (opt) =>
        opt.value !== allLabelsValue &&
        opt.value !== rejectionLabel &&
        (!requireCandidates || (calibrated[opt.value]?.candidates?.length || 0) > 0),
    )?.value ?? null;

  if (isNerEntities) {
    return (
      firstOption(true) ||
      Object.keys(calibrated).find((key) => (calibrated[key]?.candidates?.length || 0) > 0) ||
      firstOption(false) ||
      Object.keys(calibrated)[0] ||
      null
    );
  }

  if (evaluationResult?.calibration_temperature == null) return null;
  if (!isMultilabel && !evaluationResult?.confidence_rejection_enabled) return null;
  if (!evaluationResult?.threshold_candidates?.calibrated) return null;
  return firstOption(true);
}
